/**
 * Sequence Summary Export — CSV builder (mirrors campaignSummaryCsv).
 *
 * Produces a downloadable operator report of a sequence's overall performance plus its
 * per-step enrollment funnel. Reuses the campaign export's formula-injection guard and
 * downloadCsv helper so CSV-safety behavior stays consistent across exports.
 *
 * Delivery Rate is the REAL SendGrid "delivered" rate (delivered/sent from
 * sequence_message_history.delivered_at) — the same as the sequence analytics page — accurate since
 * the 2026-07-29 delivered-capture repair. "Accepted Rate" (sent - failed)/sent is also emitted as the
 * fallback for pre-repair sequences whose delivery events are unrecoverable.
 */

import type { SequenceAnalytics, SequenceStep } from "../types/sequence.types";
import type { StepCount } from "../services/sequences.service";
import { guardFormulaInjection, downloadCsv } from "./campaignSummaryCsv";

const EXPORT_TIMEZONE = "America/Chicago";
const EXPORT_TIMEZONE_LABEL = "CT";

// CSV cell: guard operator-controlled strings against formula injection, then quote/escape
// if the cell contains a comma, quote, or newline. Numbers are never injection vectors.
function csvCell(value: string | number): string {
  const s =
    typeof value === "number"
      ? String(value)
      : guardFormulaInjection(value ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function row(cells: (string | number)[]): string {
  return cells.map(csvCell).join(",");
}

function pct(numer: number, denom: number): string {
  return denom > 0 ? ((numer / denom) * 100).toFixed(1) : "0.0";
}

export function buildSequenceSummaryCsv(
  sequenceName: string,
  status: string,
  analytics: SequenceAnalytics,
  steps: SequenceStep[],
  stepCounts: Map<string, StepCount>,
): string {
  const sent = analytics.messages_sent;
  const failed = analytics.messages_failed;
  // REAL SendGrid "delivered" events (sequence_message_history.delivered_at); accurate for sends after
  // the 2026-07-29 delivered-capture repair. `accepted` (sent - failed) is the fallback for pre-repair
  // sequences whose delivery events are unrecoverable.
  const delivered = analytics.messages_delivered ?? 0;
  const accepted = Math.max(0, sent - failed);
  const now = new Date().toLocaleString("en-US", { timeZone: EXPORT_TIMEZONE });

  const lines: string[] = [];
  lines.push(row(["Sequence Summary Export"]));
  lines.push(row(["Sequence", sequenceName]));
  lines.push(row(["Status", status]));
  lines.push(row([`Exported (${EXPORT_TIMEZONE_LABEL})`, now]));
  lines.push("");
  lines.push(row(["Metric", "Value"]));
  lines.push(row(["Total Enrolled", analytics.total_enrolled]));
  lines.push(row(["Active", analytics.active_count]));
  lines.push(row(["Completed", analytics.completed_count]));
  lines.push(row(["Cancelled", analytics.cancelled_count]));
  lines.push(row(["Messages Sent", sent]));
  lines.push(row(["Delivered", delivered]));
  lines.push(row(["Accepted (not failed)", accepted]));
  lines.push(row(["Failed", failed]));
  lines.push(row(["Delivery Rate %", pct(delivered, sent)]));
  lines.push(row(["Accepted Rate %", pct(accepted, sent)]));
  lines.push(row(["Open Rate %", pct(analytics.emails_opened, sent)]));
  lines.push(
    row([
      "Click Rate %",
      pct(analytics.emails_clicked, analytics.emails_opened),
    ]),
  );
  lines.push("");
  lines.push(
    row(["Step Name", "Step Type", "Reached", "Active Here", "Completed"]),
  );
  for (const step of steps) {
    const c = stepCounts.get(step.id);
    lines.push(
      row([
        step.name ?? "(unnamed)",
        step.step_type,
        c?.total_passed ?? 0,
        c?.active_count ?? 0,
        c?.completed_count ?? 0,
      ]),
    );
  }
  return lines.join("\r\n");
}

export function downloadSequenceSummaryCsv(
  sequenceName: string,
  status: string,
  analytics: SequenceAnalytics,
  steps: SequenceStep[],
  stepCounts: Map<string, StepCount>,
): void {
  const csv = buildSequenceSummaryCsv(
    sequenceName,
    status,
    analytics,
    steps,
    stepCounts,
  );
  const slug =
    sequenceName
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "sequence";
  downloadCsv(`sequence-${slug}-summary.csv`, csv);
}
